import type {
	ConfigFormField,
	ConfigFormPayload,
	ConfigFormValueField,
} from "./protocol.js"
import { isValueField } from "./protocol.js"

/**
 * The author-facing builder for a plugin's configuration form.
 *
 * The form is a declaration, not a UI: the plugin describes the fields it wants
 * and Livespace renders them in its own design system, validates them, and
 * stores the values. The plugin gets them back in `context.config`.
 *
 * The builder is mutable and chainable — every `add*` returns `this` — because
 * that is how authors write it in one expression at the top of their script.
 */

type TextOptions = {
	helpText?: string
	required?: boolean
	placeholder?: string
	minLength?: number
	maxLength?: number
	pattern?: string
	default?: string
}

type NumberOptions = {
	helpText?: string
	required?: boolean
	min?: number
	max?: number
	step?: number
	default?: number
}

type TextareaOptions = {
	helpText?: string
	required?: boolean
	maxLength?: number
	placeholder?: string
	default?: string
}

type CheckboxOptions = {
	helpText?: string
	default?: boolean
}

type SecretOptions = {
	helpText?: string
	required?: boolean
	placeholder?: string
}

type SelectOptions = {
	helpText?: string
	required?: boolean
	default?: string
}

/**
 * Copies only the keys the author actually set. Spreading the options object
 * wholesale would emit `{ required: undefined }`, which survives the
 * `postMessage` structured clone and then reads host-side as "the author said
 * something about this" rather than "the author said nothing".
 */
const withDefined = <T extends object>(base: T, options: object): T => {
	const result: Record<string, unknown> = { ...(base as object) } as Record<
		string,
		unknown
	>
	for (const [key, value] of Object.entries(options)) {
		if (value !== undefined) {
			result[key] = value
		}
	}
	return result as T
}

export class ConfigFormBuilder {
	private readonly fields: ConfigFormField[] = []
	private readonly keys = new Set<string>()

	private claimKey(key: string): void {
		if (typeof key !== "string" || key.length === 0) {
			throw new Error("[LsPlugin] config field key must be a non-empty string.")
		}
		if (this.keys.has(key)) {
			// Two fields under one key would race to write the same config entry,
			// and only one would survive the save — silently.
			throw new Error(
				`[LsPlugin] duplicate config field key "${key}". Each field needs its own key.`,
			)
		}
		this.keys.add(key)
	}

	addTextInput(key: string, label: string, options: TextOptions = {}): this {
		this.claimKey(key)
		this.fields.push(withDefined({ kind: "text", key, label }, options))
		return this
	}

	addNumberInput(
		key: string,
		label: string,
		options: NumberOptions = {},
	): this {
		this.claimKey(key)
		this.fields.push(withDefined({ kind: "number", key, label }, options))
		return this
	}

	addTextarea(key: string, label: string, options: TextareaOptions = {}): this {
		this.claimKey(key)
		this.fields.push(withDefined({ kind: "textarea", key, label }, options))
		return this
	}

	addCheckbox(key: string, label: string, options: CheckboxOptions = {}): this {
		this.claimKey(key)
		this.fields.push(withDefined({ kind: "checkbox", key, label }, options))
		return this
	}

	addSelect(
		key: string,
		label: string,
		selectOptions: { value: string; label: string }[],
		options: SelectOptions = {},
	): this {
		this.claimKey(key)
		if (!Array.isArray(selectOptions) || selectOptions.length === 0) {
			throw new Error(`[LsPlugin] select "${key}" needs at least one option.`)
		}
		this.fields.push(
			withDefined(
				{ kind: "select", key, label, options: selectOptions },
				options,
			),
		)
		return this
	}

	/**
	 * A secret your backend needs, such as a third-party API key. The admin's
	 * browser encrypts it for your backend; it never appears in `config`, and
	 * your plugin's page never receives it. See "Your backend" in the docs.
	 */
	addSecretInput(
		key: string,
		label: string,
		options: SecretOptions = {},
	): this {
		this.claimKey(key)
		this.fields.push(withDefined({ kind: "secret", key, label }, options))
		return this
	}

	/** A heading with optional prose. Produces no config value. */
	addSection(title: string, description?: string): this {
		this.fields.push(withDefined({ kind: "section", title }, { description }))
		return this
	}

	/** A line of prose between fields. Produces no config value. */
	addHelpText(text: string): this {
		this.fields.push({ kind: "helpText", text })
		return this
	}

	/** The wire payload. A snapshot — later `add*` calls do not mutate it. */
	build(): ConfigFormPayload {
		return { fields: this.fields.map((field) => ({ ...field })) }
	}
}

export const createConfigForm = (): ConfigFormBuilder => new ConfigFormBuilder()

/** The declared defaults, as a config object. Used to seed a fresh admin form. */
export const configFormDefaults = (
	payload: ConfigFormPayload,
): Record<string, string | number | boolean> => {
	const defaults: Record<string, string | number | boolean> = {}
	for (const field of payload.fields) {
		if (
			isValueField(field) &&
			(field as ConfigFormValueField).default !== undefined
		) {
			defaults[field.key] = (field as ConfigFormValueField).default as
				| string
				| number
				| boolean
		}
	}
	return defaults
}
